import type { ComponentProps } from 'react';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion } from 'framer-motion';
import { kebabCase } from 'lodash';
import { useGetAllFeatureFlagStates } from '@frameio/feature-flags';
import { addValueToMutableObject } from '@frameio/utils/';
import {
  AlertDialog,
  AlertDialogPrimaryAction,
  AlertDialogSecondaryAction,
  Box,
  Dialog,
  IconButton2,
  List,
  ListItem,
  ListItemContent,
  Text,
  theme,
  Tooltip,
  useIsAppleOs,
} from '@frameio/vapor';
import {
  IconApp,
  IconArrowCounterclockwise,
  IconChatBubbleMore,
  IconEdit,
  IconImage,
  IconPanelAssetViewer,
  IconPinComments,
  type IconSizes,
  IconX,
  IconZoomIn,
} from '@frameio/vapor-icons';
import { SearchFieldWithI18n } from '../../components/SearchField';
import { TOOLTIP_IDS, tooltipStore } from '../../onboarding';
import type { Command } from '..';
import {
  categoryLabel,
  categorySubtitle,
  getCommandList,
} from '../COMMAND_LIST';
import { CONFLICTS_ALLOWED, CONFLICTS_ALLOWED_WILDCARD } from '../consts';
import {
  type CommandConflictInfo,
  KeyboardShortcutsProvider,
  useCustomShortcuts,
} from '../context';
import {
  toCommandCategoryEnum,
  useKeyboardShortcutsTracking,
} from '../hooks/useKeyboardShortcutsTracking';
import { detectShortcutConflicts } from '../hooks/useShortcutConflicts';
import type { CommandCategories } from '../types/Command';
import type { CommandKeys } from '../types/CommandKeys';
import { getCanonicalDisplayOrder } from '../utils/getKeyboardShortcutString';
import { CommandRegistryContext } from './CommandRegistryContext';
import styles from './CommandRegistryHelpDialog.module.css';
import { CommandRegistryHelpDialogRow } from './CommandRegistryHelpDialogRow';
import TEXT from './generated/TEXT/CommandRegistryHelpDialog';

TEXT.DEFINE({
  /** Dialog title for keyboard shortcuts */
  dialogTitle: 'Keyboard Shortcuts',
  /** Dialog close label for keyboard shortcuts */
  dialogCloseLabel: 'Close keyboard shortcuts dialog',
  /** Empty state text when no shortcuts match the search */
  noResults: 'No matches found.',
  /** Search input placeholder for keyboard shortcuts */
  placeholder: 'Search keyboard shortcuts…',
  /** Separator label between primary and alternative shortcuts */
  or: 'or',
  /** Button label to reset all customized shortcuts within a category */
  resetSection: 'Reset section customizations',
  /** Tooltip shown when the section reset button is disabled because one or
   *  more of its shortcuts would conflict if reverted to default. */
  resetSectionBlocked:
    "One or more shortcuts in this section can't be reset due to conflicts",
  resetConfirmation: {
    /** Title for reset section confirmation dialog */
    title: 'Reset Section Customizations',
    /** Details for reset section confirmation dialog */
    details: (category: string) =>
      `Are you sure you want to reset all "${category}" customizations? This action cannot be undone.`,
    /** Confirm button label for reset section dialog */
    confirm: 'Reset Section',
    /** Cancel button label for reset section dialog */
    cancel: 'Cancel',
  },
  actionModifiers: {
    /** Action modifier label for mouse click shortcuts */
    click: 'Click',
    /** Action modifier label for mouse drag shortcuts */
    drag: 'Drag',
    /** Action modifier label for press-and-hold shortcuts */
    hold: 'Hold',
  },
  customized: {
    /** Tooltip describing a customized shortcut */
    tooltip:
      'This shortcut has been customized and can be modified in your settings',
    /** Chip label for customized shortcut */
    chip: 'Customized',
    /** Tooltip for the revert button inside the Customized pill */
    revertTooltip: 'Reset shortcut to default',
  },
});
type VaporIcon = typeof IconZoomIn;
type IconForCategoryProps = {
  displayCategory: CommandCategories;
  size?: IconSizes;
  color?: string;
};
const IconForCategory = memo(
  ({ displayCategory, ...rest }: IconForCategoryProps) => {
    const iconByCategory: Record<CommandCategories, VaporIcon> = {
      locate: IconZoomIn,
      manipulate: IconEdit,
      objects: IconApp,
      panels: IconPanelAssetViewer,
      viewing: IconImage,
      comments: IconChatBubbleMore,
      anchoredComments: IconPinComments,
    };
    const Icon = iconByCategory[displayCategory];
    return <Icon {...rest} />;
  }
);

type Props = {
  initialSearchQuery?: string;
  isOpen: boolean;
  isShareContext?: boolean;
  isXKeyEnabled?: boolean;
  onRequestClose?: () => void;
} & Partial<Omit<ComponentProps<typeof Dialog>, 'aria-label' | 'width'>>;
export function CommandRegistryHelpDialog({
  initialSearchQuery = '',
  isOpen,
  isShareContext,
  onRequestClose,
  ...rest
}: Props) {
  const inputRef = useRef(null);
  const registry = useContext(CommandRegistryContext);
  const isApple = useIsAppleOs() ?? false;
  const [searchTerm, setSearchTerm] = useState(initialSearchQuery);
  // fetching all of the feature flags
  const featureFlagStates = useGetAllFeatureFlagStates();
  // if the command is behind a feature flag, we want to know which ones are disabled
  const disabledFeatureFlagNames: string[] = useMemo(
    () =>
      featureFlagStates
        .filter((flag) => !flag.enabled)
        .map((flag) => flag.name),
    [featureFlagStates]
  );
  // Commands as currently registered (with user customizations already applied
  // by the registry). Used to detect shortcut conflicts in the modal.
  const registeredCommands = useMemo(() => {
    if (!registry) return [];
    return Array.from(registry.getAll().values());
  }, [registry]);

  const checkWouldConflict = useCallback(
    (keys: string[], forCommandId: string): CommandConflictInfo[] => {
      if (!keys.length) return [];
      const canonical = getCanonicalDisplayOrder(keys as CommandKeys[])
        .map((k) => k.toLowerCase())
        .join('+');
      return (
        registeredCommands
          .filter(
            (cmd) =>
              cmd.id !== forCommandId &&
              // Wildcard-allowed commands (e.g. marquee/pan) are non-customizable
              // tool shortcuts the user has no way to move — treat them as if
              // they don't claim their keys at all so customizations onto those
              // combos aren't blocked.
              !CONFLICTS_ALLOWED_WILDCARD.includes(cmd.id)
          )
          .filter((cmd) => {
            const cmdKeys = cmd.keys || cmd.alternativeKeys;
            if (!cmdKeys || !cmdKeys.length) return false;
            const cmdCanonical = getCanonicalDisplayOrder(
              cmdKeys as CommandKeys[]
            )
              .map((k) => k.toLowerCase())
              .join('+');
            return cmdCanonical === canonical;
          })
          // Skip pairs explicitly allowed to share a combination (e.g. the
          // Find-in-Document / Find-in-Transcript / Find-in-Project trio), so
          // committing onto an allowed sibling doesn't trigger the pre-save
          // conflict guard.
          .filter(
            (cmd) =>
              !CONFLICTS_ALLOWED.some(
                (allowedGroup) =>
                  allowedGroup.includes(forCommandId) &&
                  allowedGroup.includes(cmd.id)
              )
          )
          .map((cmd) => ({
            id: cmd.id,
            displayLabel: cmd.displayLabel ?? '',
            category: '',
            isCustomizable: cmd.isCustomizable,
          }))
      );
    },
    [registeredCommands]
  );

  const customShortcuts = useCustomShortcuts();
  const { trackReset } = useKeyboardShortcutsTracking();

  // Set of command ids whose ShortcutCaptureInput is currently recording.
  // Used to defer visual "this row is customized" indicators (the purple chip
  // on the row + the per-section Reset button) until the user blurs, so they
  // don't flicker mid-edit.
  const [recordingIds, setRecordingIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const handleRowRecordingChange = useCallback(
    (id: string, isRecording: boolean) => {
      setRecordingIds((prev) => {
        const has = prev.has(id);
        if (isRecording === has) return prev;
        const next = new Set(prev);
        if (isRecording) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    []
  );

  // Confirmation dialog state for the "Reset section customizations" button.
  // `isShowing` drives the AlertDialog's enter/exit animation; `info` holds
  // the body content. We only clear `info` once the exit animation completes
  // (`onExited`) so the body text stays in place during the fade-out instead
  // of vanishing the moment the user clicks Cancel/Confirm.
  const [isResetConfirmShowing, setIsResetConfirmShowing] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState<{
    category: CommandCategories;
    categoryLabel: string;
    ids: ReadonlyArray<string>;
  } | null>(null);
  const handleConfirmReset = useCallback(() => {
    if (resetConfirmation) {
      customShortcuts?.resetMany(resetConfirmation.ids);
      trackReset({
        scope: 'Category',
        commandCategory: toCommandCategoryEnum(resetConfirmation.category),
      });
    }
    setIsResetConfirmShowing(false);
  }, [customShortcuts, resetConfirmation, trackReset]);
  const handleCancelReset = useCallback(() => {
    setIsResetConfirmShowing(false);
  }, []);

  // Map of command id → original (non-customized) keys. Required when rendering
  // a row's ShortcutCaptureInput so reset returns to the right default.
  // Prefers `keys` (the registry primary) over `alternativeKeys` so the input
  // matches what `displayKeys` shows on non-customizable rows.
  const defaultKeysById = useMemo(() => {
    const map = new Map<string, ReadonlyArray<CommandKeys>>();
    getCommandList().forEach((category) => {
      category.orderedCommands.forEach((cmd) => {
        const keys = cmd.keys || cmd.alternativeKeys;
        if (keys) map.set(cmd.id, keys);
      });
    });
    return map;
  }, []);

  // Runs `detectShortcutConflicts` against a *simulated* command set where the
  // given ids have been swapped back to their registered defaults. Lets the
  // per-row and section reset buttons block ahead of time when reverting
  // would resurrect a conflict (e.g. user cleared A's default `5`, gave `5`
  // to B, then tries to revert A). Respects `CONFLICTS_ALLOWED` because it
  // reuses the same detector.
  const checkResetConflicts = useCallback(
    (ids: ReadonlyArray<string>): Record<string, CommandConflictInfo[]> => {
      if (ids.length === 0) return {};
      const idSet = new Set(ids);
      const simulated = registeredCommands.map((cmd) => {
        if (!idSet.has(cmd.id)) return cmd;
        const defaultKeys = defaultKeysById.get(cmd.id);
        return {
          ...cmd,
          keys: defaultKeys,
          alternativeKeys: undefined,
        };
      });
      const allConflicts = detectShortcutConflicts(simulated);
      const result: Record<string, CommandConflictInfo[]> = {};
      ids.forEach((id) => {
        const entries = allConflicts[id];
        if (entries && entries.length > 0) result[id] = entries;
      });
      return result;
    },
    [registeredCommands, defaultKeysById]
  );

  const getAnchorId = (str: string) =>
    `keyboard-shortcuts-dialog-${kebabCase(str)}` as const;
  // Stable identities so the row's React.memo can bail out on dialog
  // re-renders that don't actually change the row's data (e.g. when
  // `recordingIds` updates for a sibling row).
  const getActionModifierLabel = useCallback((modifier: string) => {
    switch (modifier) {
      case 'Click':
        return TEXT.actionModifiers.click;
      case 'Drag':
        return TEXT.actionModifiers.drag;
      case 'Hold':
        return TEXT.actionModifiers.hold;
      default:
        return modifier;
    }
  }, []);
  const rowText = useMemo(
    () => ({
      or: TEXT.or,
    }),
    []
  );

  const normalizeForSearch = (value: string) =>
    value
      // Normalize "NFD" = Unicode Normalization Form Decomposition,
      // Splits out diacritics into base + combining marks
      .normalize('NFD')
      // Normalize to lowercase
      .toLocaleLowerCase()
      // Remove diacritics, e.g. "é" -> "e"
      .replace(/\p{Diacritic}/gu, '')
      // Trim whitespace
      .trim();

  const categories = useMemo(() => {
    if (!registry || !isOpen) return {};
    const isCommandBehindDisabledFeatureFlag = (
      featureFlagArray: ReadonlyArray<string>
    ) => {
      return featureFlagArray.some((featureFlag) =>
        disabledFeatureFlagNames.includes(featureFlag)
      );
    };
    // Define the categories in the order from getCommandList
    const orderedCategories: Record<string, Command[]> =
      getCommandList().reduce((acc, { categoryName }) => {
        addValueToMutableObject(acc, categoryName, []);
        return acc;
      }, {});
    // Populate the categories with commands
    Array.from(registry?.getShortcuts(), ([__shortcut, commandIds]) =>
      commandIds.map((commandId) => registry?.get(commandId))
    )
      .flat(1)
      .filter(
        (command) =>
          !command.disabled &&
          command.displayLabel !== '' &&
          !(isShareContext && command.hideInShares) &&
          (!command.featureFlag ||
            !isCommandBehindDisabledFeatureFlag(command.featureFlag))
      )
      .forEach((command) => {
        const displayCategory = command.displayCategory;
        if (!displayCategory) {
          return;
        }
        if (orderedCategories[displayCategory]) {
          if (command.index !== undefined) {
            orderedCategories[displayCategory][command.index] = command;
          } else {
            orderedCategories[displayCategory].push(command);
          }
        } else {
          // If the category is not predefined, add it as a new category
          orderedCategories[displayCategory] = [command];
        }
      });
    return orderedCategories;
  }, [registry, isOpen, disabledFeatureFlagNames, isShareContext]);
  // Memoized so dialog re-renders that don't touch the inputs (e.g. recording-
  // state changes) reuse the same array — and, critically, the same per-row
  // command refs — letting React.memo on the row skip reconciliation.
  const filteredDisplayCategories: readonly [
    CommandCategories,
    readonly (Command & { keyMatchPositions?: readonly boolean[] })[],
  ][] = useMemo(
    () =>
      Object.entries(categories)
        .map(([displayCategory, commands]) => {
          const searchTermNormalized = normalizeForSearch(searchTerm);
          const displayCategoryNormalized = normalizeForSearch(displayCategory);
          const isCategoryMatch =
            !searchTermNormalized ||
            displayCategoryNormalized.includes(searchTermNormalized);
          return [
            displayCategory as CommandCategories,
            (commands as Array<Command>)
              ?.map((command) => {
                const keyMatchPositions =
                  searchTermNormalized &&
                  command.keys &&
                  getCanonicalDisplayOrder(command.keys).map((key) => {
                    const cmdOrCtrl = isApple ? 'Command Cmd' : 'Control Ctrl';
                    const keyForSearch = key === 'Meta' ? cmdOrCtrl : key;
                    return normalizeForSearch(keyForSearch).includes(
                      searchTermNormalized
                    );
                  });
                const labelNormalized = command?.displayLabel
                  ? normalizeForSearch(command.displayLabel)
                  : '';
                const isLabelMatch =
                  !searchTermNormalized ||
                  labelNormalized.includes(searchTermNormalized);
                const matched =
                  isCategoryMatch ||
                  isLabelMatch ||
                  (Array.isArray(keyMatchPositions) &&
                    keyMatchPositions.some(Boolean));
                if (!matched) return undefined;
                // Preserve the original `command` reference when there's no
                // active search — without that, every render returns a new
                // `{ ...command }` object and React.memo on the row can't
                // bail out on unrelated dialog re-renders.
                return keyMatchPositions
                  ? { ...command, keyMatchPositions }
                  : command;
              })
              .filter(Boolean) as readonly Command[],
          ] as const;
        })
        .filter(([___displayCategory, commands]) => commands?.length > 0) as [
        CommandCategories,
        Command[],
      ][],
    [categories, searchTerm, isApple]
  );

  // The first command (in rendered order) whose row will surface the
  // ShortcutCaptureInput. Used to anchor the onboarding discovery tooltip to
  // exactly one row — and to re-anchor it as the visible list changes with
  // search/filter input.
  const firstAnchoredCommandId = useMemo(() => {
    for (const [, commands] of filteredDisplayCategories) {
      for (const command of commands) {
        if (command.isCustomizable && defaultKeysById.has(command.id)) {
          return command.id;
        }
      }
    }
    return undefined;
  }, [filteredDisplayCategories, defaultKeysById]);

  // Treat engagement with the highlighted row's edit affordance as an implicit
  // dismissal of the discovery tooltip. The anchored row only transitions to
  // recording via its edit pencil (`ShortcutCaptureInput.clearKeys`) because
  // its input is `readOnly` while tokens are present, so flipping into
  // `recordingIds` is a reliable proxy for "user clicked edit inside the
  // highlight." Dismissing here persists via the tooltip store so the user
  // won't see the discovery tooltip again on subsequent opens.
  useEffect(() => {
    if (firstAnchoredCommandId && recordingIds.has(firstAnchoredCommandId)) {
      tooltipStore.dismissTooltip(
        TOOLTIP_IDS.CUSTOM_SHORTCUTS_CAPTURE,
        'Completed'
      );
    }
  }, [firstAnchoredCommandId, recordingIds]);

  return (
    <Dialog
      aria-labelledby="KeyboardShortcutsDialog"
      enableClickOutsideToClose={true}
      hasOutermostTheme={true}
      initialFocusRef={inputRef}
      isShowing={isOpen}
      onExited={() => {
        // After the backdrop is gone, free the tooltip slot so a tooltip this
        // dialog was covering can reclaim it (waiting until now avoids it
        // re-detecting the still-animating backdrop and releasing again).
        // releaseActive only fires if the discovery tooltip is still active
        // (user never dismissed it); reconsiderPending then reselects the
        // covered tooltip regardless, so a yielded one isn't stranded.
        tooltipStore.releaseActive(TOOLTIP_IDS.CUSTOM_SHORTCUTS_CAPTURE, {
          retry: false,
        });
        tooltipStore.reconsiderPending();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && searchTerm !== '') {
          event.stopPropagation();
          setSearchTerm('');
        }
      }}
      onRequestClose={() => {
        setSearchTerm('');
        onRequestClose?.();
      }}
      size="large"
      style={{ backgroundColor: theme.colors.components.modal.background }}
      {...rest}
    >
      <div className={styles.dialogHeader}>
        <div className={styles.dialogTitleGroup}>
          <Text
            as="h2"
            color="primary"
            id="KeyboardShortcutsDialog"
            style={{ lineHeight: '24px' }}
            variant="primary"
          >
            {TEXT.dialogTitle}
          </Text>
        </div>
        <IconButton2
          icon={<IconX />}
          label={TEXT.dialogCloseLabel}
          onPress={() => {
            setSearchTerm('');
            onRequestClose?.();
          }}
          size="small"
          variant="tertiary"
        />
      </div>

      <KeyboardShortcutsProvider
        checkResetConflicts={checkResetConflicts}
        checkWouldConflict={checkWouldConflict}
      >
        <Box position="relative">
          <div className={styles.scrollContainer}>
            <div className={styles.listWrapper}>
              <List variant="nav">
                {Object.keys(categories)
                  .filter(
                    (cat) =>
                      (categories[cat as CommandCategories] as Command[])
                        ?.length > 0
                  )
                  .map((cat: string) => {
                    const displayCategory = cat as CommandCategories;
                    const matchCount =
                      filteredDisplayCategories.find(
                        ([filteredDisplayCategory]) =>
                          filteredDisplayCategory === displayCategory
                      )?.[1]?.length || 0;
                    return (
                      <ListItem
                        key={displayCategory}
                        isDisabled={matchCount <= 0}
                        onAction={() =>
                          document
                            .querySelector(`#${getAnchorId(displayCategory)}`)
                            ?.scrollIntoView({ behavior: 'smooth' })
                        }
                      >
                        <ListItemContent
                          adornmentBefore={
                            <IconForCategory
                              displayCategory={
                                displayCategory as CommandCategories
                              }
                            />
                          }
                          label={
                            searchTerm && matchCount
                              ? `${categoryLabel(
                                  displayCategory
                                )} (${matchCount})`
                              : categoryLabel(displayCategory)
                          }
                        />
                      </ListItem>
                    );
                  })}
              </List>
            </div>

            <SearchFieldWithI18n
              ref={inputRef}
              autoCapitalize="off"
              autoCorrect="off"
              debounce={0}
              enableClearButton={true}
              enableSpinner={false}
              isSearching={false}
              muted={true}
              onRequestSearch={(value) => setSearchTerm(value)}
              placeholder={TEXT.placeholder}
              query={searchTerm}
            />

            {filteredDisplayCategories.map(([category, commands]) => {
              const customizedIdsInSection = commands
                .filter((command) => command?.isCustomized)
                .map((command) => command.id);
              // Show the Reset section button when there's at least one
              // customized row that's NOT currently being recorded. So the
              // button only disappears when the lone customization in the
              // section is the one the user is actively editing — otherwise
              // the button stays available to reset the other customizations.
              const settledCustomizedIds = customizedIdsInSection.filter(
                (id) => !recordingIds.has(id)
              );
              const shouldShowResetSection = settledCustomizedIds.length > 0;
              // Block the section reset when reverting any of its customized
              // rows would resurrect a conflict (e.g. the row's default key
              // now lives on another customized shortcut).
              const isSectionResetBlocked =
                shouldShowResetSection &&
                Object.keys(checkResetConflicts(settledCustomizedIds)).length >
                  0;
              return (
                <Box
                  key={category}
                  id={getAnchorId(category)}
                  paddingBottom={8}
                  paddingTop={12}
                >
                  <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="space-between"
                    paddingBottom={categorySubtitle(category) ? 12 : 8}
                  >
                    <Box>
                      <Text
                        color="tertiary"
                        fontWeight="medium"
                        variant="secondary"
                      >
                        {categoryLabel(category)}
                      </Text>
                      {categorySubtitle(category) && (
                        <Text
                          color="tertiary"
                          fontWeight="regular"
                          // Vapor has no text size below 11px.
                          style={{
                            fontSize: `calc(${theme.fontSizes[50]} * 0.91)`,
                          }}
                          variant="caption"
                        >
                          {categorySubtitle(category)}
                        </Text>
                      )}
                    </Box>
                    {/* The Reset button slot stays mounted so the layout
                        reserves space (no shift when customizations come and
                        go); opacity + pointer-events handle the visible vs.
                        hidden states. */}
                    <motion.div
                      animate={{ opacity: shouldShowResetSection ? 1 : 0 }}
                      initial={false}
                      style={{
                        pointerEvents: shouldShowResetSection ? 'auto' : 'none',
                        // Inset by the rows' padding-right (spaces-8) so the
                        // section reset lines up vertically with the per-row
                        // reset buttons, which sit inside that row padding.
                        paddingRight: theme.spaces[8],
                      }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                    >
                      <Tooltip
                        delayIn={250}
                        disabled={!shouldShowResetSection}
                        title={
                          isSectionResetBlocked
                            ? TEXT.resetSectionBlocked
                            : TEXT.resetSection
                        }
                      >
                        {/* Wrapper div keeps the tooltip hoverable even when
                            the inner IconButton2 is disabled. */}
                        <div style={{ display: 'inline-flex' }}>
                          <IconButton2
                            icon={<IconArrowCounterclockwise />}
                            isDisabled={
                              !shouldShowResetSection || isSectionResetBlocked
                            }
                            label={TEXT.resetSection}
                            onPress={() => {
                              setResetConfirmation({
                                category,
                                categoryLabel: String(categoryLabel(category)),
                                ids: settledCustomizedIds,
                              });
                              setIsResetConfirmShowing(true);
                            }}
                            size="small"
                            variant="tertiary"
                          />
                        </div>
                      </Tooltip>
                    </motion.div>
                  </Box>
                  <Box
                    backgroundColor="primary"
                    borderRadius={8}
                    overflow="hidden"
                  >
                    {commands.map((command) => (
                      <CommandRegistryHelpDialogRow
                        key={command?.id}
                        command={command}
                        defaultKeys={defaultKeysById.get(command.id)}
                        getActionModifierLabel={getActionModifierLabel}
                        isApple={isApple}
                        isOnboardingAnchor={
                          command.id === firstAnchoredCommandId
                        }
                        isRecording={recordingIds.has(command.id)}
                        onRecordingChange={handleRowRecordingChange}
                        paddingBottom={4}
                        paddingTop={4}
                        searchTerm={searchTerm}
                        text={rowText}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}

            {filteredDisplayCategories.length === 0 && (
              <Box paddingBottom={24} paddingTop={24}>
                <Text color="tertiary" variant="secondary">
                  {TEXT.noResults}
                </Text>
              </Box>
            )}
          </div>
        </Box>
        <AlertDialog
          actions={
            <>
              <AlertDialogSecondaryAction onClick={handleCancelReset}>
                {TEXT.resetConfirmation.cancel}
              </AlertDialogSecondaryAction>
              <AlertDialogPrimaryAction
                onClick={handleConfirmReset}
                variant="destructive"
              >
                {TEXT.resetConfirmation.confirm}
              </AlertDialogPrimaryAction>
            </>
          }
          details={
            resetConfirmation
              ? TEXT.resetConfirmation
                  .details(resetConfirmation.categoryLabel)
                  ?.toString() ?? ''
              : ''
          }
          isShowing={isResetConfirmShowing}
          // Clear the underlying info only after the exit animation finishes
          // so the body text stays rendered during the fade-out.
          onExited={() => setResetConfirmation(null)}
          title={TEXT.resetConfirmation.title}
          variant="notice"
          // Stack above the help-modal Dialog (both default to zIndices.modal,
          // so the AlertDialog's backdrop wouldn't cover the help modal at
          // equal z-index). `popover` sits one tier above `modal` in Vapor.
          zIndex={theme.zIndices.popover}
        />
      </KeyboardShortcutsProvider>
    </Dialog>
  );
}
